import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Pencil, Trash2, Layers, FolderOpen, List } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCatForm = { name_en: '', name_ar: '', icon: '', image: '', sort_order: 0, is_active: true };
const emptySubForm = { category_id: '', name_en: '', name_ar: '', icon: '', image: '', sort_order: 0, is_active: true };

export default function CategoriesPage() {
  const { t, lang } = useLanguage();
  const [priceLists, setPriceLists] = useState([]);
  const [selectedPriceList, setSelectedPriceList] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [activeTab, setActiveTab] = useState('categories');
  
  // Category dialog state
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catDeleteDialogOpen, setCatDeleteDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState(emptyCatForm);
  const [catToDelete, setCatToDelete] = useState(null);
  
  // Subcategory dialog state
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subDeleteDialogOpen, setSubDeleteDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState(emptySubForm);
  const [subToDelete, setSubToDelete] = useState(null);
  
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  // Load price lists on mount
  useEffect(() => {
    axios.get(`${API}/price-lists`).then(res => {
      setPriceLists(res.data);
      const defaultPL = res.data.find(p => p.is_default);
      if (defaultPL) setSelectedPriceList(defaultPL.id);
      else if (res.data.length > 0) setSelectedPriceList(res.data[0].id);
    });
  }, []);

  const loadData = useCallback(() => {
    if (!selectedPriceList) return;
    Promise.all([
      axios.get(`${API}/categories?price_list_id=${selectedPriceList}`),
      axios.get(`${API}/subcategories?price_list_id=${selectedPriceList}`)
    ]).then(([catRes, subRes]) => {
      setCategories(catRes.data);
      setSubcategories(subRes.data);
    }).catch(console.error);
  }, [selectedPriceList]);

  useEffect(() => { loadData(); }, [loadData]);

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;
  
  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? getName(cat) : '-';
  };

  const filteredSubcategories = filterCategory === 'all' 
    ? subcategories 
    : subcategories.filter(s => s.category_id === filterCategory);

  // Category handlers
  const openAddCat = () => { setEditingCat(null); setCatForm(emptyCatForm); setCatDialogOpen(true); };
  const openEditCat = (cat) => {
    setEditingCat(cat);
    setCatForm({ name_en: cat.name_en, name_ar: cat.name_ar, icon: cat.icon || '', image: cat.image || '', sort_order: cat.sort_order, is_active: cat.is_active });
    setCatDialogOpen(true);
  };
  const openDeleteCat = (cat) => { setCatToDelete(cat); setCatDeleteDialogOpen(true); };

  const handleSaveCat = async () => {
    if (!catForm.name_en.trim() || !catForm.name_ar.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingCat) {
        await axios.put(`${API}/categories/${editingCat.id}`, catForm);
      } else {
        await axios.post(`${API}/categories`, { ...catForm, price_list_id: selectedPriceList });
      }
      toast.success(t('savedSuccess'));
      setCatDialogOpen(false);
      loadData();
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCat = async () => {
    if (!catToDelete) return;
    try {
      await axios.delete(`${API}/categories/${catToDelete.id}`);
      toast.success(t('deletedSuccess'));
      setCatDeleteDialogOpen(false);
      setCatToDelete(null);
      loadData();
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  // Subcategory handlers
  const openAddSub = () => { 
    setEditingSub(null); 
    setSubForm({ ...emptySubForm, category_id: categories[0]?.id || '' }); 
    setSubDialogOpen(true); 
  };
  const openEditSub = (sub) => {
    setEditingSub(sub);
    setSubForm({ 
      category_id: sub.category_id, 
      name_en: sub.name_en, 
      name_ar: sub.name_ar, 
      icon: sub.icon || '', 
      image: sub.image || '',
      sort_order: sub.sort_order, 
      is_active: sub.is_active 
    });
    setSubDialogOpen(true);
  };
  const openDeleteSub = (sub) => { setSubToDelete(sub); setSubDeleteDialogOpen(true); };

  const handleSaveSub = async () => {
    if (!subForm.name_en.trim() || !subForm.name_ar.trim() || !subForm.category_id) { 
      toast.error('Please fill all required fields'); 
      return; 
    }
    setSaving(true);
    try {
      if (editingSub) {
        await axios.put(`${API}/subcategories/${editingSub.id}`, subForm);
      } else {
        await axios.post(`${API}/subcategories`, { ...subForm, price_list_id: selectedPriceList });
      }
      toast.success(t('savedSuccess'));
      setSubDialogOpen(false);
      loadData();
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSub = async () => {
    if (!subToDelete) return;
    try {
      await axios.delete(`${API}/subcategories/${subToDelete.id}`);
      toast.success(t('deletedSuccess'));
      setSubDeleteDialogOpen(false);
      setSubToDelete(null);
      loadData();
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const selectedPriceListName = priceLists.find(p => p.id === selectedPriceList);

  return (
    <div className="fade-in" data-testid="categories-admin-page">
      {/* Price List Selector */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
        <List className="h-5 w-5 text-[#0056A6]" />
        <div className="flex-1">
          <Label className="text-sm text-slate-600">{t('selectPriceList')}</Label>
          <select 
            value={selectedPriceList} 
            onChange={(e) => setSelectedPriceList(e.target.value)}
            className="mt-1 flex h-9 w-full max-w-xs items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="price-list-select"
          >
            {priceLists.map(pl => (
              <option key={pl.id} value={pl.id}>{getName(pl)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{t('manageCategories')}</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            {t('categories')} ({categories.length})
          </TabsTrigger>
          <TabsTrigger value="subcategories" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            {t('subcategories')} ({subcategories.length})
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="flex justify-end mb-4">
            <Button onClick={openAddCat} className="bg-[#0056A6] hover:bg-[#004E93] shadow-lg shadow-blue-500/20" data-testid="add-category-btn">
              <Plus className="h-4 w-4 me-1.5" />
              {t('addCategory')}
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead>{t('nameEn')}</TableHead>
                  <TableHead>{t('nameAr')}</TableHead>
                  <TableHead className="text-center">{t('subcategories')}</TableHead>
                  <TableHead className="text-center">{t('products')}</TableHead>
                  <TableHead className="text-center">{t('status')}</TableHead>
                  <TableHead className="text-end">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map(cat => (
                  <TableRow key={cat.id} data-testid={`category-row-${cat.id}`}>
                    <TableCell className="font-medium text-slate-800">{cat.name_en}</TableCell>
                    <TableCell className="text-slate-600">{cat.name_ar}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="bg-indigo-50 text-indigo-600 border-0">{cat.subcategory_count || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="bg-blue-50 text-[#0056A6] border-0">{cat.product_count || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={cat.is_active ? "default" : "secondary"} className={cat.is_active ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                        {cat.is_active ? t('active') : t('inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditCat(cat)} data-testid={`edit-category-${cat.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => openDeleteCat(cat)} data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-400">{t('noCategories')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Subcategories Tab */}
        <TabsContent value="subcategories">
          <div className="flex justify-between items-center mb-4">
            <select 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)}
              className="flex h-9 w-48 items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="filter-category-select"
            >
              <option value="all">{t('allCategories')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{getName(cat)}</option>
              ))}
            </select>
            <Button onClick={openAddSub} className="bg-[#0056A6] hover:bg-[#004E93] shadow-lg shadow-blue-500/20" data-testid="add-subcategory-btn">
              <Plus className="h-4 w-4 me-1.5" />
              {t('addSubcategory')}
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead>{t('nameEn')}</TableHead>
                  <TableHead>{t('nameAr')}</TableHead>
                  <TableHead>{t('category')}</TableHead>
                  <TableHead className="text-center">{t('products')}</TableHead>
                  <TableHead className="text-center">{t('status')}</TableHead>
                  <TableHead className="text-end">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubcategories.map(sub => (
                  <TableRow key={sub.id} data-testid={`subcategory-row-${sub.id}`}>
                    <TableCell className="font-medium text-slate-800">{sub.name_en}</TableCell>
                    <TableCell className="text-slate-600">{sub.name_ar}</TableCell>
                    <TableCell className="text-slate-500">{getCategoryName(sub.category_id)}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="bg-blue-50 text-[#0056A6] border-0">{sub.product_count || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={sub.is_active ? "default" : "secondary"} className={sub.is_active ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                        {sub.is_active ? t('active') : t('inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditSub(sub)} data-testid={`edit-subcategory-${sub.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => openDeleteSub(sub)} data-testid={`delete-subcategory-${sub.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSubcategories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-400">{t('noSubcategories')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Add/Edit Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editingCat ? t('editCategory') : t('addCategory')}</DialogTitle>
            <DialogDescription>{editingCat ? t('editCategory') : t('addCategory')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ImageUpload
              label={t('image')}
              value={catForm.image}
              onChange={(url) => setCatForm(p => ({ ...p, image: url }))}
            />
            <div>
              <Label>{t('nameEn')} *</Label>
              <Input value={catForm.name_en} onChange={e => setCatForm(p => ({ ...p, name_en: e.target.value }))} className="mt-1.5" data-testid="category-name-en" />
            </div>
            <div>
              <Label>{t('nameAr')} *</Label>
              <Input value={catForm.name_ar} onChange={e => setCatForm(p => ({ ...p, name_ar: e.target.value }))} className="mt-1.5" dir="rtl" data-testid="category-name-ar" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('sortOrder')}</Label>
                <Input type="number" value={catForm.sort_order} onChange={e => setCatForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} className="mt-1.5" data-testid="category-sort-order" />
              </div>
              <div className="flex items-end justify-end gap-3 pb-1">
                <Label className="text-sm">{catForm.is_active ? t('active') : t('inactive')}</Label>
                <Switch checked={catForm.is_active} onCheckedChange={v => setCatForm(p => ({ ...p, is_active: v }))} data-testid="category-active-switch" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCatDialogOpen(false)} data-testid="category-cancel-btn">{t('cancel')}</Button>
            <Button onClick={handleSaveCat} disabled={saving} className="bg-[#0056A6] hover:bg-[#004E93]" data-testid="category-save-btn">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Delete Confirmation */}
      <Dialog open={catDeleteDialogOpen} onOpenChange={setCatDeleteDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-category-dialog">
          <DialogHeader>
            <DialogTitle>{t('deleteCategory')}</DialogTitle>
            <DialogDescription>{t('deleteConfirm')}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">{catToDelete && getName(catToDelete)}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCatDeleteDialogOpen(false)}>{t('cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteCat} data-testid="delete-confirm-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Add/Edit Dialog */}
      <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="subcategory-dialog">
          <DialogHeader>
            <DialogTitle>{editingSub ? t('editSubcategory') : t('addSubcategory')}</DialogTitle>
            <DialogDescription>{editingSub ? t('editSubcategory') : t('addSubcategory')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ImageUpload
              label={t('image')}
              value={subForm.image}
              onChange={(url) => setSubForm(p => ({ ...p, image: url }))}
            />
            <div>
              <Label>{t('category')} *</Label>
              <select 
                value={subForm.category_id} 
                onChange={(e) => setSubForm(p => ({ ...p, category_id: e.target.value }))}
                className="mt-1.5 flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="subcategory-category-select"
              >
                <option value="">{t('selectCategory')}</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{getName(cat)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>{t('nameEn')} *</Label>
              <Input value={subForm.name_en} onChange={e => setSubForm(p => ({ ...p, name_en: e.target.value }))} className="mt-1.5" data-testid="subcategory-name-en" />
            </div>
            <div>
              <Label>{t('nameAr')} *</Label>
              <Input value={subForm.name_ar} onChange={e => setSubForm(p => ({ ...p, name_ar: e.target.value }))} className="mt-1.5" dir="rtl" data-testid="subcategory-name-ar" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('sortOrder')}</Label>
                <Input type="number" value={subForm.sort_order} onChange={e => setSubForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} className="mt-1.5" data-testid="subcategory-sort-order" />
              </div>
              <div className="flex items-end justify-end gap-3 pb-1">
                <Label className="text-sm">{subForm.is_active ? t('active') : t('inactive')}</Label>
                <Switch checked={subForm.is_active} onCheckedChange={v => setSubForm(p => ({ ...p, is_active: v }))} data-testid="subcategory-active-switch" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSubDialogOpen(false)} data-testid="subcategory-cancel-btn">{t('cancel')}</Button>
            <Button onClick={handleSaveSub} disabled={saving} className="bg-[#0056A6] hover:bg-[#004E93]" data-testid="subcategory-save-btn">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Delete Confirmation */}
      <Dialog open={subDeleteDialogOpen} onOpenChange={setSubDeleteDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-subcategory-dialog">
          <DialogHeader>
            <DialogTitle>{t('deleteSubcategory')}</DialogTitle>
            <DialogDescription>{t('deleteConfirm')}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">{subToDelete && getName(subToDelete)}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSubDeleteDialogOpen(false)}>{t('cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteSub} data-testid="delete-subcategory-confirm-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
